import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/api';
import { FiEdit2, FiTrash, FiPlus } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import Modal from '../components/Modal';
import UserForm from '../components/UserForm';
import { useLocation } from 'react-router-dom';
import { Heading } from '../components/ui/heading';
import { Timestamp } from '../components/ui/timestamp';

// User interface from the API
interface User {
  user_id: string;
  username: string;
  email: string;
  organization_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Form data interface for creating/updating users
interface UserFormData {
  user_id?: string;
  username: string;
  email: string;
  password?: string;
  organization_id: string;
  role: string;
  is_active?: boolean;
}

const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const { user: currentUser } = useAuth();
  const location = useLocation();
  
  // If we were passed an organization ID from the organization management page,
  // we'll filter users by that organization
  const filterOrgId = location.state?.orgId;
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      let endpoint = '/v1/auth/admin/users';
      
      // If filtering by organization, add the organization_id parameter
      if (filterOrgId) {
        endpoint += `?organization_id=${filterOrgId}`;
      }
      
      const data = await fetchWithAuth(endpoint);
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = () => {
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };
  
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };
  
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/users/${userId}`, {
        method: 'DELETE'
      });
      
      // Deletion was successful if we got here (no error was thrown)
      // Remove the user from the state without needing a server refresh
      setUsers(currentUsers => 
        currentUsers.filter(user => user.user_id !== userId)
      );
      
      // Show success message
      const tempMessage = document.createElement('div');
      tempMessage.className = 'fixed top-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded-md shadow-md';
      tempMessage.innerText = 'User deleted successfully';
      document.body.appendChild(tempMessage);
      
      // Remove message after 3 seconds
      setTimeout(() => {
        tempMessage.remove();
      }, 3000);
    } catch (err) {
      setError('Failed to delete user. Please try again.');
      console.error(err);
    }
  };
  
  const handleSubmitUser = async (userData: UserFormData) => {
    try {
      if (userData.user_id) {
        // Update existing user
        await fetchWithAuth(`/v1/auth/admin/users/${userData.user_id}`, {
          method: 'PUT',
          body: JSON.stringify(userData)
        });
      } else {
        // Create new user
        await fetchWithAuth('/v1/auth/admin/users', {
          method: 'POST',
          body: JSON.stringify(userData)
        });
      }
      
      // Close modal and refresh users
      setIsModalOpen(false);
      fetchUsers();
    } catch (err) {
      throw err;
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg text-red-800">
        <p>{error}</p>
        <button 
          onClick={fetchUsers}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md text-red-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <Heading level={1} weight="bold" className="text-foreground">
          {filterOrgId ? 'Organization Users' : 'All Users'}
        </Heading>
        <button 
          onClick={handleCreateUser}
          className="px-4 py-2 bg-primary text-secondary rounded-md flex items-center hover:bg-primary/90 transition-colors"
        >
          <IconWrapper icon={FiPlus} className="mr-2" />
          Add New User
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left border-b-2 border-gray-200">
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Username</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Role</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Created</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-4 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{user.username}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <Timestamp value={user.created_at} format="absolute" />
                  </td>
                  <td className="px-4 py-3 flex space-x-2">
                    <button 
                      className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                      onClick={() => handleEditUser(user)}
                    >
                      <IconWrapper icon={FiEdit2} size={16} />
                    </button>
                    <button 
                      className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                      disabled={user.username === currentUser?.username}
                      onClick={() => user.username !== currentUser?.username && handleDeleteUser(user.user_id)}
                    >
                      <IconWrapper icon={FiTrash} size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6 text-sm text-gray-500">
        <p>* You cannot delete your own account</p>
      </div>
      
      {/* User Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedUser ? 'Edit User' : 'Create User'}
      >
        <UserForm
          user={selectedUser}
          onSubmit={handleSubmitUser}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default UsersManagement;